/**
 * "Submit News" — registered users share news for the Things to Know feed.
 * Admin + trusted-publisher accounts auto-publish; everyone else's post is
 * queued for admin review.
 */
import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Newspaper, Upload, X, Loader2, BadgeCheck } from "lucide-react";

const CATEGORIES = [
  "World", "Politics", "Business", "Technology", "Science", "Health",
  "Environment", "Entertainment", "Sports", "Good News", "Odd News",
  "Weather", "War", "Disasters", "News You Can Use",
];

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function NewsSubmit() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    title: "",
    category: "News You Can Use",
    subcategory: "",
    short_description: "",
    long_article: "",
    website_link: "",
    youtube_link: "",
    reviewer_note: "",
  });
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const autoPublish = useMemo(
    () => !!(user && (user.is_admin || user.is_trusted_publisher)),
    [user]
  );

  if (!user) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <div className="wofy-card">
          <Newspaper className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--wofy-primary)" }} />
          <h2 className="font-display text-2xl">Sign in to submit news</h2>
          <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            You must be signed in so we can attribute your post correctly.
          </p>
          <Link to={`/login?next=${encodeURIComponent("/things-to-know/submit")}`} className="wofy-btn-primary mt-6 inline-flex">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  const onAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (images.length >= 6) return toast.error("Max 6 images");
    if (file.size > 3 * 1024 * 1024) return toast.error("Each image < 3 MB");
    const dataUrl = await fileToDataUrl(file);
    setImages((p) => [...p, dataUrl]);
    e.target.value = "";
  };

  const removeImage = (i) => setImages((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (f.title.trim().length < 6) return toast.error("Title needs at least 6 characters");
    if (f.short_description.trim().length < 10) return toast.error("Short description needs at least 10 characters");
    if (f.long_article.trim().length < 30) return toast.error("Long article needs at least 30 characters");
    setBusy(true);
    try {
      const { data } = await http.post("/news/submissions", { ...f, images });
      if (data.auto_published) {
        toast.success("Published!");
        nav(`/things-to-know/${data.article_id}`);
      } else {
        toast.success("Thank you — your submission is under admin review.");
        nav("/things-to-know");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10" data-testid="news-submit-page">
      <div className="mb-8">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Newspaper size={14} className="inline mr-1.5 -mt-0.5" /> SUBMIT NEWS
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">Share news the world can use.</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Submit a news item, a local story, or a resource you think our community should know about.
          {" "}
          {autoPublish ? (
            <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "#1D4ED8" }}>
              <BadgeCheck className="w-3.5 h-3.5" />
              You're a trusted publisher — your posts publish instantly.
            </span>
          ) : (
            "All submissions are reviewed by admins before publication."
          )}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Field label="News Title *">
          <input
            data-testid="ns-title"
            value={f.title}
            onChange={(e) => upd("title", e.target.value)}
            placeholder="A short, clear headline"
            className="wofy-input"
            required
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Category *">
            <select
              data-testid="ns-category"
              value={f.category}
              onChange={(e) => upd("category", e.target.value)}
              className="wofy-input"
            >
              {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </Field>
          <Field label="Subcategory (optional)">
            <input
              data-testid="ns-subcategory"
              value={f.subcategory}
              onChange={(e) => upd("subcategory", e.target.value)}
              placeholder="e.g. Local Alerts, Health Tips"
              className="wofy-input"
              maxLength={80}
            />
          </Field>
        </div>

        <Field label="Short Description *" hint={`${f.short_description.length}/500`}>
          <textarea
            data-testid="ns-short"
            value={f.short_description}
            onChange={(e) => upd("short_description", e.target.value)}
            placeholder="One-paragraph summary — shows on cards and previews."
            rows={3}
            maxLength={500}
            className="wofy-input"
            required
          />
        </Field>

        <Field label="Long Article *" hint={`${f.long_article.length}/25000`}>
          <textarea
            data-testid="ns-long"
            value={f.long_article}
            onChange={(e) => upd("long_article", e.target.value)}
            placeholder="The full story — supports plain paragraphs. Use blank lines between paragraphs."
            rows={10}
            maxLength={25000}
            className="wofy-input"
            required
          />
        </Field>

        <Field label={`Images (up to 6, < 3 MB each) — ${images.length}/6`}>
          <div className="flex flex-wrap gap-2 mb-2">
            {images.map((src, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 rounded-full bg-white/85 text-[#14213D] border border-[#14213D]/20 p-0.5" aria-label="Remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-full border text-sm hover:bg-[color:var(--wofy-surface)]" style={{ borderColor: "var(--wofy-border)" }}>
            <Upload className="w-4 h-4" />
            Add image
            <input type="file" accept="image/*" onChange={onAddImage} className="hidden" data-testid="ns-image" />
          </label>
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Website / Source link">
            <input
              data-testid="ns-website"
              value={f.website_link}
              onChange={(e) => upd("website_link", e.target.value)}
              placeholder="https://…  (or social media URL)"
              className="wofy-input"
              maxLength={800}
            />
          </Field>
          <Field label="YouTube link">
            <input
              data-testid="ns-youtube"
              value={f.youtube_link}
              onChange={(e) => upd("youtube_link", e.target.value)}
              placeholder="https://youtube.com/watch?v=…"
              className="wofy-input"
              maxLength={800}
            />
          </Field>
        </div>

        <Field label="Comment for the reviewer (optional)" hint="Not published — only admins see this.">
          <textarea
            data-testid="ns-note"
            value={f.reviewer_note}
            onChange={(e) => upd("reviewer_note", e.target.value)}
            placeholder="Anything the admin should know before publishing?"
            rows={2}
            maxLength={1000}
            className="wofy-input"
          />
        </Field>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            Posting as <b>{user.name || user.email}</b>{" "}
            {user.is_trusted_publisher && (
              <BadgeCheck className="inline w-3.5 h-3.5 -mt-0.5" style={{ color: "#1D4ED8" }} />
            )}
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="ns-submit"
            className="wofy-btn-primary disabled:opacity-60"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : (autoPublish ? "Publish now" : "Submit for review")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>{label}</span>
        {hint && <span className="text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{hint}</span>}
      </div>
      {children}
    </label>
  );
}
